// src/types/service.ts
export interface PriceRange {
  min: number;
  max: number;
}

export interface DurationRange {
  min: number;
  max: number;
}

export interface ServiceDescription {
  short: string;
  detailed: string;
}

export interface ServiceOption {
  id: string;
  name: string;
  description?: string;
  additionalPrice: number;
  additionalDuration: number;
}

export interface ServiceVariation {
  id: string;
  name: string;
  description?: string;
  options: ServiceOption[];
}

export interface Service {
  _id: string;
  stylistId: string;
  name: string;
  description: ServiceDescription;
  category: string;
  basePrice: PriceRange;
  baseDuration: DurationRange;
  variations: ServiceVariation[];
  isActive: boolean;
  image?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceFormData {
  name: string;
  description: {
    short: string;
    detailed: string;
  };
  category: string;
  basePrice: PriceRange;
  baseDuration: DurationRange;
  variations: ServiceVariation[];
  isActive: boolean;
  image?: string;
}

export interface ServiceCategory {
  id: string;
  name: string;
}

// List of common service categories for salons
export const DEFAULT_SERVICE_CATEGORIES: ServiceCategory[] = [
  { id: 'haircut', name: 'Haircut' },
  { id: 'coloring', name: 'Coloring' },
  { id: 'styling', name: 'Styling' },
  { id: 'treatments', name: 'Treatments' },
  { id: 'extensions', name: 'Extensions' },
  { id: 'facial', name: 'Facial' },
  { id: 'makeup', name: 'Makeup' },
  { id: 'nails', name: 'Nails' },
  { id: 'massage', name: 'Massage' },
  { id: 'wellness', name: 'Wellness' },
  { id: 'fitness', name: 'Fitness' },
  { id: 'therapy', name: 'Therapy' },
  { id: 'waxing', name: 'Waxing' },
  { id: 'other', name: 'Other' }
];

// Default variations for hair services
export const DEFAULT_HAIR_VARIATIONS: ServiceVariation[] = [
  {
    id: 'hair-length',
    name: 'Hair Length',
    description: 'Select based on your current hair length',
    options: [
      {
        id: 'short',
        name: 'Short',
        description: 'Above shoulders',
        additionalPrice: 0,
        additionalDuration: 0
      },
      {
        id: 'medium',
        name: 'Medium',
        description: 'Shoulder to mid-back',
        additionalPrice: 10,
        additionalDuration: 15
      },
      {
        id: 'long',
        name: 'Long',
        description: 'Below mid-back',
        additionalPrice: 20,
        additionalDuration: 30
      }
    ]
  },
  {
    id: 'hair-complexity',
    name: 'Hair Complexity',
    description: 'Select based on your hair type and thickness',
    options: [
      {
        id: 'simple',
        name: 'Simple',
        description: 'Straight, fine hair',
        additionalPrice: 0,
        additionalDuration: 0
      },
      {
        id: 'medium',
        name: 'Medium',
        description: 'Wavy, medium thickness',
        additionalPrice: 5,
        additionalDuration: 10
      },
      {
        id: 'complex',
        name: 'Complex',
        description: 'Curly, thick, or coarse hair',
        additionalPrice: 15,
        additionalDuration: 20
      }
    ]
  }
];

export interface ServiceVariations {
  type: 'hair' | 'size' | 'complexity';
  options: {
      lengths?: string[];
      thicknesses?: string[];
      sizes?: string[];
      levels?: string[];
  };
  priceFactors: {
      byLength?: Map<string, number>;
      byThickness?: Map<string, number>;
      bySize?: Map<string, number>;
      byLevel?: Map<string, number>;
  };
  _id?: string;
}

export interface ServiceItem {
  _id: string;
  name: string;
  category: string;
  subcategory: string;
  description: string | {
      short?: string;
      full?: string;
  };
  basePrice: {
      min: number;
      max: number;
      suggestedPrice?: number;
  };
  baseDuration: {
      min: number;
      max: number;
      suggestedDuration?: number;
  };
  variations?: ServiceVariations;
  requiresConsultation: boolean;
  customRequirements: string[];
  preparationInstructions?: string[];
  aftercareInstructions?: string[];
  isEnabled: boolean;
  skillLevel?: string;
}